
import { deleteRequest, patchRequest, postRequest } from '@/plugins/api-axios'
import { getURL } from '@/utils/axios'
import { deserialise, serialise } from 'kitsu-core'
import ProjectMixin from '@/_mixins/project'
import DialogTemplate from '@/_components/dialog/template/template.vue'
import { openDialog } from '@/_components/dialog/dialog'
import ConfirmationDialog from '@/views/Project/ch/components/confirmationDialog.vue'
import TimeDuration from '@/views/Project/ch/components/workAlert/timeDuration.vue'

type AlertType =
    | 'maxWorkPerDay'
    | 'maxWorkPerWeek'
    | 'minConsecutiveRestPerDay'
    | 'minConsecutiveRestPerWeek'
    | 'minConsecutiveRestPerPeriod'

class WorkAlert {
    id = null as Maybe<string>
    alertType = 'maxWorkPerDay' as AlertType
    avgPeriods = null as Maybe<number>
    periodSeconds = null as Maybe<number>
    thresholdSeconds = 1
    validFrom = null as Maybe<string>
    validTo = null as Maybe<string>
    constraints?: Record<string, { writable: boolean }>

    constructor(data?: WorkAlert) {
        if (data) Object.assign(this, data)
    }
}

export default {
    name: 'WorkAlertSettings',
    mixins: [ProjectMixin],
    components: {
        DialogTemplate,
        TimeDuration,
    },
    props: {
        companyDetails: {
            type: Object,
        },
        items: {
            type: Array,
            default: () => [],
        },
        hierarchy: {
            type: Object,
        },
    },
    data() {
        return {
            isAlertDialogOpen: false,
            isSavingAlert: false,
            workAlerts: this.items as WorkAlert[],
            alert: null as Maybe<WorkAlert>,
            alertExists: false,
            startDateMenu: false,
            endDateMenu: false,
            today: new Date().toISOString().substr(0, 10),
        }
    },
    computed: {
        canDelete() {
            if (this.companyDetails) {
                return this.companyDetails.constraints?.workAlertDefinitions?.writable === true
            }

            return this.hierarchy?.constraints?.workAlertDefinitions?.writable === true
        },
        rules() {
            return {
                required: (value: any) => !!value || this.$t('fe_common_validation.required'),
                avgPeriods: (value: number) => {
                    if (!this.alert) return true
                    if (
                        this.alert.alertType === 'maxWorkPerDay' ||
                        this.alert.alertType === 'maxWorkPerWeek'
                    ) {
                        return value >= 1
                    }
                    return true
                },
                fromDate: (value: Maybe<string>) => {
                    if (!value || !this.alert?.validTo) return true
                    return this.isoDate(value) <= this.isoDate(this.alert.validTo)
                },
                toDate: (value: Maybe<string>) => {
                    if (!value || !this.alert?.validFrom) return true
                    return this.isoDate(value) >= this.isoDate(this.alert.validFrom)
                },
            }
        },
        alertTimeDurationSettings() {
            return {
                maxWorkPerDay: { hours: true, minutes: true },
                maxWorkPerWeek: { hours: true, minutes: true },
                minConsecutiveRestPerDay: { hours: true, minutes: true },
                minConsecutiveRestPerWeek: { hours: true, minutes: true },
                minConsecutiveRestPerPeriod: {
                    weeks: true,
                    days: true,
                    hours: true,
                    minutes: true,
                },
            }
        },
        alertTypes() {
            return [
                {
                    text: this.$t('fe_contractors["workAlert.enums_maxWorkPerDay"]'),
                    value: 'maxWorkPerDay',
                },
                {
                    text: this.$t('fe_contractors["workAlert.enums_maxWorkPerWeek"]'),
                    value: 'maxWorkPerWeek',
                },
                {
                    text: this.$t('fe_contractors["workAlert.enums_minConsecutiveRestPerDay"]'),
                    value: 'minConsecutiveRestPerDay',
                },
                {
                    text: this.$t('fe_contractors["workAlert.enums_minConsecutiveRestPerWeek"]'),
                    value: 'minConsecutiveRestPerWeek',
                },
                {
                    text: this.$t('fe_contractors["workAlert.enums_minConsecutiveRestPerPeriod"]'),
                    value: 'minConsecutiveRestPerPeriod',
                },
            ]
        },
        restTimeMoreThanWorkPeriod() {
            if (!this.alert || this.alert.alertType !== 'minConsecutiveRestPerPeriod') return false
            return this.alert.periodSeconds > this.alert.thresholdSeconds
        },
    },
    watch: {
        items: {
            handler(items: WorkAlert[]) {
                this.workAlerts = items
            },
        },
        isAlertDialogOpen(value: boolean) {
            if (!value) {
                this.startDateMenu = false
                this.endDateMenu = false
            }
        },
        startDateMenu() {
            this.validate()
        },
        endDateMenu() {
            this.validate()
        },
    },
    methods: {
        toggleDateMenu(menu: 'startDateMenu' | 'endDateMenu', value: boolean) {
            setTimeout(() => {
                this[menu] = value
            }, 0)
        },
        isEffective(from: Maybe<string>, to: Maybe<string>) {
            const isoToday = new Date(this.toIsoDate(this.today))
            const fromDate = from ? new Date(from) : null
            const toDate = to ? new Date(to) : null

            if (fromDate && toDate) {
                return fromDate <= isoToday && toDate >= isoToday
            }
            if (toDate) {
                return toDate >= isoToday
            }
            if (fromDate) {
                return fromDate <= isoToday
            }
        },
        isoDate(value: Maybe<string>, format = 'YYYY.MM.DD') {
            return this.formatDate(value, this.getDeviceTimeZone, format)
        },
        toIsoDate(value: Maybe<string>) {
            if (!value) return null
            return this.formatDateTimeWithTimeZone(value, '00:00', this.getDeviceTimeZone)
        },
        isReadonly(key: string) {
            if (!this.alert?.id) return false
            return !this.alert?.constraints?.[key] || this.alert.constraints[key].writable === false
        },
        async validate() {
            // this is a hack to case following
            // 1. user create new alert, select type, fill in the "custom" hours/minutes...
            // 2. while the hours/minutes... is still being focused, click on Save
            // The Save click's hander happens first (while the hours/minutes... is not yet being emitted back) which cause the validation to fail, which cause the click action to be ignored, need to click again to submit
            await new Promise((res) => setTimeout(res, 0))

            // @ts-ignore
            return this.$refs.form.validate()
        },
        handleAlertTypeChanged(value: string) {
            if (!this.alert) return
            this.alertExists = false
            this.alert.avgPeriods =
                value === 'maxWorkPerDay' || value === 'maxWorkPerWeek' ? 1 : null
            this.alert.periodSeconds = value === 'minConsecutiveRestPerPeriod' ? 1 : 0
        },
        closeAlertDialog() {
            this.isAlertDialogOpen = false
            this.alert = null
            this.alertExists = false
        },
        addOrEditAlert(alert?: WorkAlert) {
            this.alert = new WorkAlert(alert)
            this.isAlertDialogOpen = true
        },

        async deleteAlert(alert: WorkAlert) {
            let ok = await openDialog(ConfirmationDialog, {
                message: 'fe_contractors["workAlert.delete_confirmation"]',
                title: this.$t('fe_contractors["workAlert.heading_delete_alert"]'),
                name: this.$t(`fe_contractors['workAlert.enums_${alert.alertType}']`),
            })

            if (!ok) return

            await deleteRequest(
                getURL({
                    path: `/workAlertDefinitions/${alert.id}`,
                    base: this.contractorBaseUrl,
                }),
                {}
            )

            this.$emit('alert:deleted')
        },
        getAlertKey(alert: Maybe<WorkAlert>) {
            if (!alert) return ''
            return (alert.alertType || '') + (alert.avgPeriods || '') + (alert.periodSeconds || '')
        },
        async saveAlert() {
            const valid = await this.validate()

            if (!valid || !this.hierarchy || !this.alert || this.restTimeMoreThanWorkPeriod) return

            const existingAlert = this.workAlerts.find(
                (a) => this.getAlertKey(a) === this.getAlertKey(this.alert)
            )

            if (existingAlert && !this.alert.id) {
                this.alertExists = true
                return
            }

            const attributes: Record<string, any> = {}
            if (!this.isReadonly('alertType')) attributes.alertType = this.alert.alertType
            if (!this.isReadonly('avgPeriods') && this.alert.avgPeriods)
                attributes.avgPeriods = this.alert.avgPeriods
            if (!this.isReadonly('periodSeconds') && this.alert.periodSeconds)
                attributes.periodSeconds = this.alert.periodSeconds
            if (!this.isReadonly('thresholdSeconds'))
                attributes.thresholdSeconds = this.alert.thresholdSeconds
            if (!this.isReadonly('validFrom')) attributes.validFrom = this.alert.validFrom || null
            if (!this.isReadonly('validTo')) attributes.validTo = this.alert.validTo || null

            if (!this.alert.id) {
                if (this.companyDetails) {
                    attributes.companyDetails = {
                        data: {
                            id: this.companyDetails.id,
                            type: 'companyDetails',
                        },
                    }
                }
                attributes.hierarchy = {
                    data: {
                        id: this.hierarchy.id,
                        type: 'contractorHierarchy',
                    },
                }
            } else {
                attributes.id = this.alert.id
            }

            try {
                this.isSavingAlert = true
                const payload = serialise('workAlertDefinition', attributes)

                const request = this.alert.id ? patchRequest : postRequest
                const path = this.alert.id
                    ? `/workAlertDefinitions/${this.alert.id}`
                    : `/workAlertDefinitions`

                const { data } = await request(
                    getURL({
                        path,
                        base: this.contractorBaseUrl,
                        params: { include: 'hierarchy' },
                    }),
                    {},
                    payload.data
                )
                const alert = deserialise(data).data

                if (!this.alert.id) {
                    this.workAlerts.push(alert)
                } else {
                    const index = this.workAlerts.findIndex((a) => a.id === alert.id)
                    this.workAlerts[index] = alert
                }

                this.alert = null
                this.$emit('update:hierarchy', alert.hierarchy.data)
                this.$emit('update:items', this.workAlerts)
                this.closeAlertDialog()
            } finally {
                this.isSavingAlert = false
            }
        },
    },
}
